import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api/client';
import type { DashboardSummary } from '../types';

import { CommandPalette } from '@/components/CommandPalette';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export function Dashboard() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const summaryData = await api.dashboard.getSummary();
        setSummary(summaryData);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    }
    void fetchData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const handleOpenCommandPalette = () => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-muted-foreground">
        <i className="bi bi-arrow-repeat text-2xl mb-3 animate-spin"></i>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <CommandPalette />
      
      {/* Page Header */}
      <div className="flex justify-between items-center pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Your financial overview at a glance</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleOpenCommandPalette}>
            ⌘ K · Quick actions
          </Button>
        </div>
      </div>

      {/* Bento Grid - Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <i className="bi bi-cash-stack text-lg text-primary"></i>
              Cash & Bank
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>
              {formatCurrency(summary?.trial_balance?.accounts?.find((a) => a.type === 'ASSET')?.debit ?? 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary?.bank_accounts_count ?? 0} accounts
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <i className="bi bi-graph-up text-lg text-green-600"></i>
              Revenue (YTD)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(
                summary?.trial_balance?.accounts
                  ?.filter((a) => a.type === 'REVENUE')
                  .reduce((sum, a) => sum + a.credit, 0) ?? 0
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">On track</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <i className="bi bi-graph-down-arrow text-lg text-red-600"></i>
              Expenses (YTD)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(
                summary?.trial_balance?.accounts
                  ?.filter((a) => a.type === 'EXPENSE')
                  .reduce((sum, a) => sum + a.debit, 0) ?? 0
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Within budget</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-primary bg-muted/50">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <i className="bi bi-lightbulb text-lg text-primary"></i>
              AI Insights
              <Badge variant="secondary" className="ml-auto">
                <i className="bi bi-sparkles mr-1"></i>
                3 new
              </Badge>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Your tax filing deadline is in <strong>28 days</strong>. GST/HST remittance of <strong>$3,250.00</strong> is due.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Trial Balance Status */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <i className="bi bi-sliders text-primary"></i>
              Trial Balance Status
            </CardTitle>
          </div>
          <Link to="/reports/trial-balance">
            <Button variant="outline" size="sm">View Full Report</Button>
          </Link>
        </CardHeader>
        <CardContent>
          {summary?.trial_balance.is_balanced ? (
            <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg mb-4">
              <i className="bi bi-check-circle-fill text-2xl text-green-600"></i>
              <div>
                <div className="font-semibold text-green-700 dark:text-green-400">Books are balanced</div>
                <div className="text-sm text-green-600 dark:text-green-500">All debits equal credits</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950/20 rounded-lg mb-4">
              <i className="bi bi-exclamation-triangle-fill text-2xl text-red-600"></i>
              <div>
                <div className="font-semibold text-red-700 dark:text-red-400">Books are out of balance</div>
                <div className="text-sm text-red-600 dark:text-red-500">
                  Difference: {formatCurrency(Math.abs(summary?.trial_balance.difference ?? 0))}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 items-center">
            <div className="text-center">
              <div className="text-sm text-muted-foreground mb-1">Total Debits</div>
              <div className="text-xl font-mono font-bold">
                {formatCurrency(summary?.trial_balance.total_debits ?? 0)}
              </div>
            </div>
            <div className="flex justify-center">
              <div className="w-px h-12 bg-border" />
            </div>
            <div className="text-center">
              <div className="text-sm text-muted-foreground mb-1">Total Credits</div>
              <div className="text-xl font-mono font-bold">
                {formatCurrency(summary?.trial_balance.total_credits ?? 0)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Overview Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <i className="bi bi-clipboard-data text-primary"></i>
              Account Summary
            </CardTitle>
            <Link to="/accounts">
              <Button variant="ghost" size="sm">View All</Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(summary?.accounts_by_type ?? {}).map(([type, count]) => (
                <div key={type} className="flex justify-between items-center py-2 border-b last:border-0">
                  <span className="text-sm">
                    {type.charAt(0) + type.slice(1).toLowerCase()}
                  </span>
                  <span className="font-mono font-medium">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <i className="bi bi-hourglass-split text-primary"></i>
              Pending Items
            </CardTitle>
            <Link to="/reconciliation">
              <Button variant="ghost" size="sm">View All</Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className={`flex justify-between items-center p-3 rounded-lg ${
                summary?.unreconciled_count && summary.unreconciled_count > 0
                  ? 'bg-[var(--accent-bg)] border border-[var(--accent)]'
                  : 'bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900'
              }`}>
                <div className="flex items-center gap-2">
                  <i className={`bi ${summary?.unreconciled_count && summary.unreconciled_count > 0 ? 'bi-exclamation-circle text-[var(--accent)]' : 'bi-check-circle text-green-600'}`}></i>
                  <span className="text-sm">Unreconciled Transactions</span>
                </div>
                <span className="font-mono font-medium">{summary?.unreconciled_count ?? 0}</span>
              </div>

              <div className="flex justify-between items-center p-3 rounded-lg bg-muted">
                <div className="flex items-center gap-2">
                  <i className="bi bi-bar-chart text-muted-foreground"></i>
                  <span className="text-sm">Tax Codes Active</span>
                </div>
                <span className="font-mono font-medium">{summary?.tax_codes_count ?? 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-lightning-charge text-primary"></i>
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Link to="/reports/trial-balance">
              <Button variant="outline" size="sm">Verify Books</Button>
            </Link>
            <Link to="/reports/tax-summary">
              <Button variant="outline" size="sm">Tax Summary</Button>
            </Link>
            <Link to="/reconciliation">
              <Button variant="outline" size="sm">Reconcile</Button>
            </Link>
            <Link to="/journal">
              <Button variant="outline" size="sm">Journal Entry</Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}